<template>
  <div>
    <oie-contact-banner></oie-contact-banner>
    <oie-message-bar
      v-if="cart.errorInLastPriceCall"
      id="warning_message"
      :text="tStatic('failedToGetPricesWarning')"
      :use-close-button="false"></oie-message-bar>

    <div class="container checkout">
      <oie-checkout-header></oie-checkout-header>

      <a :href="urlReferrer" class="checkout__back-link">{{ t('linkLabelNavigateBack') }}</a>

      <div class="checkout__page-content">
        <h1 class="checkout__page-header">
          {{ t('checkoutTitle') }}
        </h1>

        <template v-if="hasRows" class="checkout__page-text">
          {{ t('checkoutText') }}
        </template>

        <template v-else class="checkout__page-text" v-html="t('checkoutTextNoDataInCart')">
        </template>
      </div>

      <div v-if="cart.paymentIsNotOk" class="checkout-section">
        <div class="checkout__payment-error" v-html="t('paymentFailedMessage')"></div>
      </div>

      <ul v-if="hasCampaigns" class="checkout__campaigns-container">
        <li v-for="(campaign, i) in cart.campaigns" :key="`campaign_${i}`">
          <oie-campaign-chip :config="campaign"></oie-campaign-chip>
        </li>
      </ul>

      <template v-if="hasRows">
        <!-- if not authenticated (signup) -->
        <oie-checkout-sign-up
          v-if="!clientModel.isAuthenticated"
          :component-index="1"
          :event-key="variables.CHECKOUT_SIGN_UP"></oie-checkout-sign-up>

        <!-- else authenticated user -->

        <oie-checkout-addresses
          v-if="clientModel.isAuthenticated"
          :open-on-init="true"
          :component-index="1"
          :customer="customer"></oie-checkout-addresses>

        <!-- shipping -->

        <oie-checkout-shipping-types
          data-cy="shipment-type"
          :customer="customer"
          :shipping-address="checkoutPayload.deliveryAddress"
          :component-index="2"
          :event-key="variables.CHECKOUT_SHIPPING"></oie-checkout-shipping-types>

        <!-- payment -->

        <oie-checkout-select-payment
          data-cy="payment-method"
          :campaign-code.sync="checkoutPayload.campaignCode"
          :gift-card-code.sync="checkoutPayload.giftCardCode"
          :component-index="3"
          :customer="customer"
          :event-key="variables.CHECKOUT_PAYMENT"
          :enable-invoice-request-anonymous-user="enableInvoiceRequestForAnonymousUser"></oie-checkout-select-payment>

        <div :class="['checkout-container-placeholder', { 'checkout-section--not-active': !accessLastStep }]">
          <section class="checkout-products" data-cy="checkout-products">
            <div class="checkout-products__header">
              {{ tStatic('cartSumHeaderLabel') }}
              <a :href="clientModel.cartPageUrl" class="checkout-products__link">{{ t('checkoutGoToCart') }}</a>
            </div>
          </section>

          <oie-cart-products-table
            class="cart-shell-container__cart-products"
            :rows="cart.rows"
            :use-edit-mode="false"
            enable-price-edit
            @updatePrice="updatePrice"></oie-cart-products-table>

          <!-- order marking -->

          <oie-expandable-item :open-on-init="true" :heading="t('headerMarkOnOrder')">
            <validation-observer ref="invoiceOrderNoForm" tag="form" class="form-container invoice-order-no-form">
              <div class="form-container__top">
                <p v-if="t('checkoutCommentsDescription')">
                  {{ t('checkoutCommentsDescription') }}
                </p>
              </div>

              <template v-if="!isOrderRequestVisitorState">
                <validation-provider v-slot="{errors}" :rules="createRequiredInvoiceReferenceOrderNumberValidateRule" slim>
                  <oie-base-input
                    id="customerOrderNumber"
                    v-model="checkoutPayload.customerOrderNumber"
                    :textarea="false"
                    :label-text="createRequiredInvoiceReferenceOrderNumberValidateRule ? `${t('labelCustomerOrderNumber')}*` : t('labelCustomerOrderNumber')"
                    :placeholder="t('placeholderCustomerOrderNumber')"
                    :max-length="30"
                    css-classes="checkout-actions__input"
                    :error-messages="errors"
                    @blur="saveCheckoutPayload()"></oie-base-input>
                </validation-provider>

                <validation-provider v-slot="{errors}" :rules="createRequiredInvoiceReferenceOrderNumberValidateRule" slim>
                  <oie-base-input
                    id="invoiceReference"
                    v-model="checkoutPayload.invoiceReference"
                    :textarea="false"
                    :label-text="createRequiredInvoiceReferenceOrderNumberValidateRule ? `${t('labelInvoiceReference')}*` : t('labelInvoiceReference')"
                    :placeholder="t('placeholderInvoiceReference')"
                    :max-length="30"
                    css-classes="checkout-actions__input"
                    :error-messages="errors"
                    @blur="saveCheckoutPayload()"></oie-base-input>
                </validation-provider>
              </template>
            </validation-observer>
          </oie-expandable-item>

          <oie-expandable-item :open-on-init="false" :heading="t('headerCommentOnOrder')">
            <validation-observer tag="form" class="form-container invoice-order-no-form">
              <oie-base-input
                id="comment"
                v-model="checkoutPayload.comment"
                :label-text="t('placeholderCommentOnOrder')"
                :textarea="true"
                :max-length="250"
                :placeholder="t('placeholderCommentOnOrder')"
                css-classes="checkout-actions__input"
                @blur="saveCheckoutPayload()"></oie-base-input>
            </validation-observer>
          </oie-expandable-item>

          <!-- superuser things -->

          <oie-expandable-item v-if="isSuperUser" :heading="t('superuserHeader')" class="expandable-item--is-superuser">
            <div class="form-container">
              <validation-observer ref="superUserForm" tag="form" class="form-container superuser-form">
                <validation-provider
                  v-if="!checkoutPayload.superUserDoNotSendOrderConfirmation"
                  v-slot="{errors}"
                  rules="email"
                  slim>
                  <oie-base-input
                    id="overrideConfEmail"
                    v-model="checkoutPayload.superuserOverrideOrderConfirmationEmail"
                    :label-text="t('superUserOverrideOrderConfirmation')"
                    :max-length="200"
                    :placeholder="(customer && customer.email) || (checkoutPayload.deliveryAddress && checkoutPayload.deliveryAddress.username) || 'Ange en epostadress'"
                    css-classes="checkout-actions__input"
                    :error-messages="errors"
                    @blur="saveCheckoutPayload()"></oie-base-input>
                </validation-provider>

                <oie-base-checkbox
                  v-model="checkoutPayload.superUserDoNotSendOrderConfirmation"
                  :is-for="`superuser-send-order-confirmation`"
                  :text="'Skicka inte en orderbekräftelse'"
                  @input="saveCheckoutPayload()"></oie-base-checkbox>

                <oie-base-input
                  id="extraEmail"
                  v-model="checkoutPayload.superuserExtraEmailOrderConfirmation"
                  :label-text="t('superUserExtraEmail')"
                  :textarea="true"
                  :max-length="350"
                  :placeholder="t('superUserExtraEmailOrderConfirmation')"
                  css-classes="checkout-actions__input"
                  @blur="saveCheckoutPayload()"></oie-base-input>

                <oie-base-input
                  id="comment"
                  v-model="checkoutPayload.superuserGoodsComment"
                  :label-text="t('superUserGoodsComment')"
                  :textarea="true"
                  :max-length="35"
                  :placeholder="t('superUserCommentOnOrder')"
                  css-classes="checkout-actions__input"
                  @blur="saveCheckoutPayload()"></oie-base-input>

                <oie-base-checkbox
                  v-model="checkoutPayload.superuserBlockOrder"
                  :is-for="`superuser-block`"
                  :text="t('superUserBlockOrderLabel')"
                  @input="saveCheckoutPayload()"></oie-base-checkbox>
              </validation-observer>
            </div>
          </oie-expandable-item>

          <!-- summary  -->

          <oie-checkout-summary></oie-checkout-summary>

          <oie-checkout-confirm-action :checkout-payload="checkoutPayload" :customer="customer" event-key="checkout-approve-terms"></oie-checkout-confirm-action>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import previousMarket from '@/mixins/previous-market'
import httpService from '@/core/http-service'
import gtm from '@/core/enhanced-ecommerce-gtm'
import utils from '@/core/utils'
import * as variables from '@/variables/checkout-event-keys'
import _ from 'lodash'
import { listTypes } from '@/core/analytics-variables'

export default {
  mixins: [previousMarket],
  props: {
    title: {
      type: String,
      required: false,
      default: ''
    },
    text: {
      type: String,
      required: false,
      default: ''
    },
    urlReferrer: {
      type: String,
      required: false,
      default: ''
    }
  },
  data: () => ({
    checkoutPayload: {
      selectedShipping: null,
      selectedAgent: null,
      selectedCardPayment: null,
      comment: null,
      code: null,
      deliveryAddress: null,
      partDelivery: false,
      invoiceReference: null,
      customerOrderNumber: null,
      idNumber: null,
      pdfInvoiceEmail: null,
      approveTerms: null,
      checkoutSteps: null,
      orderRequestVisitor: false,
      notificationEmail: null,
      notificationPhone: null,
      superuserGoodsComment: null,
      superuserExtraEmailOrderConfirmation: null,
      superuserOverrideOrderConfirmationEmail: null,
      superuserBlockOrder: null,
      customerNumber: null,
      campaignCode: '',
      giftCardCode: ''
    },
    customer: { anonymous: true },
    toggleUserPassword: false,
    hasRunInitAnalyticsPush: false,
    hasRunCartValidState: false,
    variables: variables,
    listTypes
  }),
  computed: {
    ...mapGetters('commerce', ['cart', 'campaign', 'giftCard']),
    ...mapGetters('checkout', ['checkoutState', 'activeSteps', 'isOrderRequestVisitorState']),
    ...mapGetters(['enableGtm']),
    accessLastStep() {
      return this.activeSteps.length >= 3
    },
    enableInvoiceRequestForAnonymousUser() {
      return this.toggleUserPassword && this.checkoutPayload.deliveryAddress !== null && this.checkoutPayload.deliveryAddress !== undefined
    },
    createRequiredInvoiceReferenceOrderNumberValidateRule() {
      return this.clientModel.isRequiredInvoiceReferenceOrderNumber &&
            (this.checkoutState.selectedCardPayment === 'InvoiceRequest' || this.checkoutState.selectedCardPayment === 'Invoice') ? 'required' : ''
    },
    isSuperUser () {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    },
    superUserShowHasInvoice () {
      return this.toggleUserPassword || this.customer.anonymous
    },
    hasRows() {
      return this.cart.rows && this.cart.rows.length > 0
    },
    hasCampaigns() {
      return this.cart.campaigns && this.cart.campaigns.length > 0
    },
    confirmationEmail() {
      return this.isAuthenticated
        ? this.cart.confirmationEmail
        : this.checkoutPayload.deliveryAddress.noemail
          ? 'Skickas inte, kund saknar epostadress'
          : this.checkoutPayload.deliveryAddress.username
    }
  },
  watch: {
    cart(value) {
      if (this.enableGtm && !this.hasRunInitAnalyticsPush) {
        gtm.addCheckoutStep('begin_checkout', value)

        this.hasRunInitAnalyticsPush = true
      }

      if (!this.hasRunCartValidState) {
        if (this.cart.paymentIsNotOk) {
          this.checkoutPayload.selectedCardPayment = null
          this.setCheckout(this.checkoutPayload)
        }
        this.hasRunCartValidState = true
      }
    }
  },
  mounted() {
    if (this.checkoutState === null || this.checkoutState === undefined) {
      this.setCheckout(JSON.parse(JSON.stringify(this.checkoutPayload)))
    }

    if (this.previousMarket === null) {
      this.setPreviousMarketToCurrent()
    }

    if (!this.isPreviousMarketSameAsCurrent) {
      this.setPreviousMarketToCurrent(true)
    } else {
      this.checkoutPayload = this.checkoutState
    }

    this.subscribe('checkout-container-comment', (value) => {
      this.checkoutPayload.comment = value
    })

    this.subscribe('checkout-container-customer-id-number', (value) => {
      let idNumber = _.replace(value, /[ -]/, '')
      if (idNumber.length === 12) idNumber = idNumber.slice(2)
      this.customer.idNumber = idNumber
      this.checkoutPayload.idNumber = idNumber
    })

    this.subscribe('checkout-container-code', (value) => {
      this.checkoutPayload.code = value
    })

    this.subscribe('checkout-container-customer-type', (value) => {
      this.checkoutPayload.deliveryAddress.isCompanyCustomer = value
      this.setCheckout(this.checkoutPayload)
    })

    this.subscribe('checkout-container-payment', (value) => {
      this.checkoutPayload.selectedCardPayment = value
      this.setCheckout(this.checkoutPayload)
    })

    this.subscribe('checkout-container-shipping', (value) => {
      this.checkoutPayload.selectedShipping = value.selectedShipping
      this.checkoutPayload.selectedAgent = value.selectedAgentId
      this.checkoutPayload.useShippingAgreement = value.useShippingAgreement
      this.checkoutPayload.shippingAgreement = value.shippingAgreement

      if(this.cart.splitOrder === false) {
        this.checkoutPayload.partDelivery = false
      }

      this.setCheckout(this.checkoutPayload)
    })

    this.subscribe('checkout-container-notification', (value) => {
      this.checkoutPayload.notificationEmail = value.notificationEmail
      this.checkoutPayload.notificationPhone = value.notificationPhone

      this.setCheckout(this.checkoutPayload)
    })

    this.subscribe('checkout-part-delivery', (value) => {
      if (value === null || value === undefined) {
        value = false
      }

      this.checkoutPayload.partDelivery = value
      this.setCheckout(this.checkoutPayload)
    })

    this.subscribe('checkout-approve-terms', (value) => {
      this.checkoutPayload.approveTerms = value
    })

    this.subscribe('checkout-container-signup', (value) => {
      if (value.phone) {
        const countryObject = this.clientModel.countryList.find(c => c.value === value.countryCode)
        value.phone = utils.savePhoneNumberWithFormating(value.phone, countryObject.callingCode)
      }

      this.checkoutPayload.deliveryAddress = value
      this.toggleUserPassword = value.toggleUserPassword || false

      if (this.isSuperUser) {
        if (this.customer.anonymous) {
          if (value.noemail) {
            this.checkoutPayload.superuserOverrideOrderConfirmationEmail = null
          }
        }
      }

      this.setCheckout(this.checkoutPayload)
    })

    this.subscribe('checkout-set-address', (value) => {
      this.deliveryAddress = value
      this.checkoutPayload.deliveryAddress = value
      this.checkoutPayload.deliveryAddressId = value.id
      this.setCheckout(this.checkoutPayload)
    })

    this.subscribe('checkout-new-address', (value) => {
      this.deliveryAddress = value
    })

    this.subscribe('checkout-pdf-invoice', (pdfInvoice, pdfInvoiceEmail) => {
      if (pdfInvoice) {
        this.checkoutPayload.pdfInvoiceEmail = pdfInvoiceEmail
      } else {
        this.checkoutPayload.pdfInvoiceEmail = null
      }
      this.setCheckout(this.checkoutPayload)
    })

    if (this.activeSteps === null || this.activeSteps === undefined) {
      this.setActiveSteps([1])
    }

    if (this.clientModel.isAuthenticated) {
      httpService.get('auth/get').then(response => {
        this.customer = response.data
        let updateStore = false

        if (this.customer.settings.deliveryNotificationEmail === null) {
          this.customer.settings.deliveryNotificationEmail = this.customer.email
        }
        if (this.customer.settings.deliveryNotificationPhone === null) {
          this.customer.settings.deliveryNotificationPhone = this.customer.phone
        }

        if (!this.checkoutPayload.selectedShipping && this.customer.lastDeliveryMethod.modeOfDelivery) {
          this.checkoutPayload.selectedShipping = this.customer.lastDeliveryMethod.modeOfDelivery
          this.checkoutPayload.preferredDeliveryGroupingKey = this.customer.lastDeliveryMethod.preferredDeliveryGroupingKey
          this.checkoutPayload.selectedAgent = this.customer.lastDeliveryMethod.agentId
          updateStore = true
        }

        if (!this.checkoutPayload.selectedCardPayment && this.customer && this.customer.hasInvoice && !this.customer.invoicedBlocked) {
          this.checkoutPayload.selectedCardPayment = 'Invoice'
          updateStore = true
        }

        if (this.checkoutPayload.notificationEmail) {
          this.customer.settings.deliveryNotificationEmail = this.checkoutPayload.notificationEmail
        } else if (this.customer.settings.deliveryNotificationEmail) {
          this.checkoutPayload.notificationEmail = this.customer.settings.deliveryNotificationEmail
          updateStore = true
        }

        if (this.checkoutPayload.notificationPhone) {
          this.customer.settings.deliveryNotificationPhone = this.checkoutPayload.notificationPhone
        } else if (this.customer.settings.deliveryNotificationPhone) {
          this.checkoutPayload.notificationPhone = this.customer.settings.deliveryNotificationPhone
          updateStore = true
        }

        if (this.checkoutPayload.invoiceReference) {
          this.customer.settings.invoiceReference = this.checkoutPayload.invoiceReference
        } else if (this.customer.settings.invoiceReference) {
          this.checkoutPayload.invoiceReference = this.customer.settings.invoiceReference
        }

        if (!this.checkoutPayload.superuserGoodsComment) {
          this.checkoutPayload.superuserGoodsComment = this.customer.superUserGoodsComment
          updateStore = true
        }

        if (!this.checkoutPayload.superuserExtraEmailOrderConfirmation) {
          this.checkoutPayload.superuserExtraEmailOrderConfirmation = this.customer.superUserExtraEmailOrderConfirmation
          updateStore = true
        }

        if (updateStore) {
          this.setCheckout(this.checkoutPayload)
        }
      })
    }
  },
  methods: {
    ...mapActions('commerce', ['getCart', 'setCart']),
    ...mapActions('checkout', ['setCheckout', 'setActiveSteps']),
    saveCheckoutPayload() {
      this.setCheckout(this.checkoutPayload)
    },
    updatePrice({ row, price }) {
      httpService.post('cart/setPrice', { newPrice: { lineItemId: row, overridenPrice: price } }).then(({ data }) => {
        this.setCart(data.data)
      })
    }
  }
}
</script>

<style lang="scss">
.checkout {
  @include body-smaller;
  padding: measure(half);

  @include media(small) {
    @include body;
  }

  @include media(medium) {
    padding-top: calc(#{measure(full)} * 2);
    padding-bottom: calc(#{measure(full)} * 2);
  }

}

.checkout-products__header {
  @include body-semi-bold;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  margin-bottom: measure(half);

  @include media(small) {
    @include h2-semi-bold;
    flex-direction: row;
    align-items: center;
  }
}

.checkout > *:not(.checkout__header),
.checkout-container-placeholder > * {
  margin-bottom: measure(half);

  @include media(small) {
    margin-bottom: measure(double);
  }
}

.checkout > .checkout-products {
  margin-bottom: 0;
}

.checkout__header {
  display: flex;
  align-items: center;
  margin-bottom: measure(full);
}

.checkout__logo {
  width: 15.625rem;
  color: color(logo-blue);
}

.checkout__logo > svg {
  width: 10.375rem;
  height: auto;

  @include media(medium) {
    width: 100%;
  }
}

.checkout__back-link {
  @include body-semi-bold;
  display: inline-block;
  color: color(text-blue);
  text-decoration: none;
  margin-bottom: measure(full);

  &:hover {
    text-decoration: underline;
  }

  @include media(medium) {
    @include h3-semi-bold;
  }

  @media print {
    display: none;
  }
}

.checkout__page-content {
  margin-bottom: measure(full);
}

.checkout__page-header-container {
  display: flex;
  align-items: center;
}

.checkout__page-header {
  @include h1-semi-bold;
  margin: 0;
}

.checkout__page-print-button {
  @include body-smaller-semi-bold;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  border: none;
  background-color: transparent;
  color: color(text-blue);
  padding: 0;

  &:hover {
    cursor: pointer;
  }

  @media print {
    display: none;
  }
}

.checkout__page-print-button-icon {
  height: 1.7rem;
  margin-left: measure(quarter);
}

/* section item */

.checkout-section--not-active {
  opacity: .5;
  pointer-events: none;
}

.checkout-login-register .checkout-section {
  border: none;
  background-color: transparent;
}

.checkout-section__item {
  border-bottom: 1px solid color(border-gray);
  margin-bottom: measure(half);
  padding-bottom: measure(half);

  @include media(small) {
    margin-bottom: measure(full);
    padding-bottom: measure(full);
  }
}

.checkout-login-register .checkout-section__item {
  border-bottom: none;
  margin: 0;
  padding-top: measure(full);
  padding-bottom: 0;
}

.checkout-section__item > *:not(:last-child) {
  margin-bottom: measure(half);
}

.checkout-section__item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.checkout-section__item--message {
  padding-bottom: 0;
  border-bottom: 0;
}

.checkout-section__item--split-delivery {
  border-bottom: 0;
  padding-bottom: 0;
}

.checkout-section__item--first-item {
  border-top: 1px solid color(border-gray);
}

.checkout-login-register .checkout-section__item--first-item {
  border-top: none;
}

.checkout-section__item-icon {
  margin-bottom: 0 !important;
  flex-shrink: 0;
  margin-right: measure(half);
}

.checkout-section__item-logo {
  width: auto;
  max-height: 3.75rem;
  margin-right: measure(half);
}

/* text and message */

.checkout-section__item-text {
  @include caption;
}

.checkout-section__item-information-text {
  @include body-smaller-semi-bold;

  @include media(medium) {
    @include body-semi-bold;
  }
}

.checkout-products__link {
  @include reset-link;
  @include body-semi-bold;

  @include media(small) {
    margin-left: auto;
  }
}

/* address dropdown */

.checkout-address-dropdown {
  @include body-smaller-semi-bold;
  width: 100%;

  @include media(small) {
    width: auto;
    margin-left: auto;
  }
}

.checkout__payment-error {
  margin: 0 measure(full);
  color: color(red-1);
}

/* form-container */

.form-container > .input {
  max-width: 75ch;
}

.form-container > *:not(:first-child) {
  margin-top: measure(half);
}

.form-container__top {
  display: flex;
  flex-direction: column;
  gap: measure(eighth);
}

.form-container__top p {
  max-width: 75ch;
  margin: 0;
}

.form-container h3 {
  max-width: 75ch;
  margin: 0;
}

/* campagin chips */

.checkout__campaigns-container {
  margin: 0;
  list-style-type: none;
  padding: 0;
}

.checkout__campaigns-container >*:not(:last-child){
  margin-bottom: measure(quarter);
}
</style>
